import React, { useMemo } from "react";
import { Clock3, Loader2, Users } from "lucide-react";
import {
  TAB_UNFOLLOW,
  formatFollowerCount,
} from "../../utils/followersDeskHelpers";
import { formatPeakHour } from "../../utils/viewersDeskHelpers";
import FollowerChurnGauge from "./FollowerChurnGauge";
import FollowerNetGrowthChart from "./FollowerNetGrowthChart";
import {
  FollowMixDonut,
  HourlyHeatStrip,
  TopFollowersRanked,
} from "./FollowerChartViz";

const FollowerAnalyticsPanel = ({
  pulse,
  loading = false,
  activeTab,
  netGrowthSeries = [],
  churnStats = null,
  layout = "workspace",
}) => {
  const isUnfollowTab = activeTab === TAB_UNFOLLOW;
  const isWorkspace = layout === "workspace";

  const hourlySource = isUnfollowTab ? pulse?.unfollowByHour : pulse?.followByHour;
  const hourlyItems = Array.isArray(hourlySource)
    ? hourlySource.map((value, hour) => ({ key: `h-${hour}`, value }))
    : [];
  const hourlyHasEvents = hourlyItems.some((item) => item.value > 0);
  const hourlyPeak = isUnfollowTab
    ? pulse?.unfollowPeakHour ?? null
    : pulse?.followPeakHour ?? null;

  const topFollowers = useMemo(
    () => (pulse?.topFollowers || []).filter((row) => Number(row.count) > 0),
    [pulse?.topFollowers]
  );

  if (loading) {
    return (
      <section
        className={`bd-follower-intel bd-follower-intel-${layout}`}
        aria-label="Follower analytics"
      >
        <div className="bd-follower-intel-loading">
          <Loader2 size={14} className="animate-spin" />
          <span>Reading followers…</span>
        </div>
      </section>
    );
  }

  if (!pulse?.loaded) {
    return null;
  }

  const totalEvents = Number(pulse.totalEvents) || 0;
  const totalFollows = Number(pulse.totalFollows) || 0;
  const totalUnfollows = Number(pulse.totalUnfollows) || 0;
  const currentFollowers = Number(pulse.currentFollowers) || 0;

  return (
    <section
      className={`bd-follower-intel bd-follower-intel-${layout}`}
      aria-label="Follower analytics"
    >
      {!isWorkspace ? (
        <header className="bd-audience-analytics-head">
          <Users size={14} />
          <div>
            <h2>Follower insights</h2>
            <p>
              {totalEvents > 0
                ? `${formatFollowerCount(currentFollowers)} now · ${totalEvents.toLocaleString()} stored events.`
                : "Charts appear once follow events are recorded."}
            </p>
          </div>
        </header>
      ) : null}

      {totalEvents === 0 ? (
        <p className="bd-audience-empty bd-follower-intel-empty">
          No follow events recorded yet.
        </p>
      ) : (
        <>
          {isWorkspace ? (
            <div className="bd-follower-intel-hero">
              <span className="bd-book-kicker">Audience</span>
              <strong className="tabular-nums">{formatFollowerCount(currentFollowers)}</strong>
              <span className="bd-follower-intel-hero-meta tabular-nums">
                {totalFollows.toLocaleString()} follows · {totalUnfollows.toLocaleString()} unfollows
              </span>
            </div>
          ) : null}

          <div className="bd-follower-intel-grid">
            <FollowerNetGrowthChart series={netGrowthSeries} compact={isWorkspace} />
            <FollowerChurnGauge churn={churnStats} compact={isWorkspace} />

            {hourlyHasEvents ? (
              <article className="aud-card aud-card-compact">
                <div className="bd-book-card-head">
                  <Clock3 size={11} aria-hidden />
                  <div>
                    <span className="bd-book-kicker">
                      {isUnfollowTab ? "Unfollows by hour" : "Follows by hour"}
                    </span>
                    {hourlyPeak != null ? (
                      <em className="bd-book-caption">peaks {formatPeakHour(hourlyPeak)}</em>
                    ) : null}
                  </div>
                </div>
                <HourlyHeatStrip
                  items={hourlyItems}
                  peakIndex={hourlyPeak}
                  tone={isUnfollowTab ? "unfollow" : "follow"}
                  unitLabel={isUnfollowTab ? "unfollows" : "follows"}
                  ariaLabel={
                    isUnfollowTab
                      ? "Recorded unfollows by hour of day"
                      : "Recorded follows by hour of day"
                  }
                />
              </article>
            ) : null}

            {totalFollows + totalUnfollows > 0 ? (
              <article className="aud-card aud-card-compact">
                <div className="bd-book-card-head">
                  <Users size={11} aria-hidden />
                  <span className="bd-book-kicker">Event mix</span>
                </div>
                <FollowMixDonut
                  follows={totalFollows}
                  unfollows={totalUnfollows}
                  currentFollowers={currentFollowers}
                  compact={isWorkspace}
                />
              </article>
            ) : null}

            {!isUnfollowTab && topFollowers.length ? (
              <article className="aud-card aud-card-compact">
                <div className="bd-book-card-head">
                  <Users size={11} aria-hidden />
                  <span className="bd-book-kicker">Most events</span>
                </div>
                <TopFollowersRanked rows={topFollowers} />
              </article>
            ) : null}
          </div>
        </>
      )}
    </section>
  );
};

export default FollowerAnalyticsPanel;
